import React from 'react';
import { Box, Typography } from '@mui/material';
import { ProgramDueItem } from './gamificationTypes';
import { splitByUrgency } from './dueUrgency';

// Programs Due flyout — shared by the learner and admin bars so both read
// identically. Programs that are overdue or due within 14 days sit in a
// red-outlined "Urgent Zone"; everything else lists quietly beneath. The same
// rule puts the red "!" and outline on those programs' cards.
//
// The admin variant adds two things the learner has no use for: the program's
// completion rate across its learners, and a Review Progress CTA into Org
// Members filtered to that program.

const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const BORDER_CONTRAST = '#444444';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const TEXT_TERTIARY = '#868686';
const BRAND_LIGHT = '#F79420';
const DUE_RED = '#DA2828';
const SUCCESS = '#22C55E';

const ghostButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  px: '12px',
  py: '6px',
  borderRadius: '8px',
  cursor: 'pointer',
  border: `1px solid ${BORDER_CONTRAST}`,
  color: TEXT_SECONDARY,
  fontFamily: 'Inter, sans-serif',
  fontSize: 12.5,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  transition: 'border-color 0.15s ease, color 0.15s ease',
  '&:hover': { borderColor: BRAND_LIGHT, color: TEXT_PRIMARY }
} as const;

const panelTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  color: BRAND_LIGHT,
  lineHeight: 1,
  whiteSpace: 'nowrap'
} as const;

// Completion rate colour — red below a third, brandy below two thirds.
const completionColor = (pct: number) => (pct < 34 ? DUE_RED : pct < 67 ? BRAND_LIGHT : SUCCESS);

const DueBadge: React.FC = () => (
  <Box
    component="span"
    sx={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      bgcolor: DUE_RED,
      color: '#FDFDFD',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: 0,
      lineHeight: 1,
      flexShrink: 0
    }}
  >
    !
  </Box>
);

interface Props {
  items: ProgramDueItem[];
  /** Admin only — completion rate per program name, 0–100. */
  completionByProgram?: Record<string, number>;
  /** Admin only — renders the Review Progress CTA on each row. */
  onReviewProgress?: (programName: string) => void;
}

const ProgramsDuePanel: React.FC<Props> = ({ items, completionByProgram, onReviewProgress }) => {
  const { urgent, later } = splitByUrgency(items);
  const wide = Boolean(onReviewProgress);

  const Row: React.FC<{ item: ProgramDueItem; first: boolean; dim?: boolean }> = ({ item, first, dim }) => {
    const completion = completionByProgram?.[item.programName];
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          py: '10px',
          borderTop: first ? 'none' : `1px solid ${BORDER_DIM}`
        }}
      >
        <Box
          component="span"
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dim ? TEXT_TERTIARY : DUE_RED, flexShrink: 0 }}
        />
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            color: TEXT_PRIMARY,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {item.programName}
        </Typography>
        {typeof completion === 'number' && (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: 700,
              color: completionColor(completion),
              flexShrink: 0
            }}
          >
            {completion}%
          </Typography>
        )}
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: TEXT_SECONDARY, flexShrink: 0 }}>
          {item.due}
        </Typography>
        {onReviewProgress && (
          <Box onClick={() => onReviewProgress(item.programName)} sx={{ ...ghostButtonSx, flexShrink: 0 }}>
            Review Progress
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: wide ? 560 : 420,
        maxWidth: 'calc(100vw - 48px)',
        bgcolor: SURFACE,
        border: `1px solid ${BORDER_DIM}`,
        borderRadius: '12px',
        p: '20px'
      }}
    >
      {/* Pointer back at the chip that opened this. */}
      <Box
        sx={{
          position: 'absolute',
          top: -6,
          left: 20,
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: `6px solid ${DUE_RED}`
        }}
      />
      <Typography sx={panelTitleSx}>
        <DueBadge />
        PROGRAMS DUE
      </Typography>

      {/* Urgent Zone — overdue, or due within 14 days. Red-outlined so the
          programs that actually need attention separate from the rest. */}
      {urgent.length > 0 && (
        <Box
          sx={{
            mt: '14px',
            p: '10px 14px 4px',
            borderRadius: '10px',
            border: `1px solid ${DUE_RED}`,
            boxShadow: '0 0 12px rgba(218,40,40,0.25)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '2px' }}>
            <DueBadge />
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1.2px',
                color: DUE_RED
              }}
            >
              DUE SOON
            </Typography>
          </Box>
          {urgent.map((p, i) => (
            <Row key={p.programName} item={p} first={i === 0} />
          ))}
        </Box>
      )}

      {later.length > 0 && (
        <Box sx={{ mt: '10px' }}>
          {later.map((p, i) => (
            <Row key={p.programName} item={p} first={i === 0} dim />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProgramsDuePanel;
